import { theme as antdTheme } from 'antd';
import type { ThemeConfig } from 'antd';
import type { AliasToken } from 'antd/es/theme/interface';
import { themeShape, uiPalettes, type SemanticTokens, type ThemeMode, type UiPaletteName } from './tokens';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  ANT DESIGN, TOKENISED (docs/design/DESIGN_LANGUAGE.md).
 *
 *  Shipping AntD at its defaults — the blue, the 6px radius, the grey table header band —
 *  is the single largest reason an admin panel looks generated. Every value below comes
 *  from `theme/tokens.ts`; the only literals permitted in this file are `'transparent'`,
 *  `'none'` and numbers, and `antdTheme.test.ts` proves it by walking the output.
 *
 *  `cssVar: true, hashed: false` is the coexistence setting the audit confirmed:
 *
 *    • `cssVar: true`  makes AntD emit CSS custom properties instead of baking literal
 *                      colours into thousands of generated class names. Theme switching
 *                      becomes a variable swap rather than a full stylesheet regeneration
 *                      — and, because the output is scoped to a CLASS rather than :root,
 *                      it is also what lets `ThemeScope` render two themes on one page.
 *    • `hashed: false` drops the `css-dev-only-do-not-override-xxx` hash from selectors.
 *                      With the hash on, AntD's specificity climbs and Tailwind utilities
 *                      stop winning — which is when people reach for `!important`.
 *
 *  Layer ordering (see `styles/index.css`) does the rest. Neither setting is cosmetic.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/**
 * The focus ring, and the ONE documented exception to "no shadows in dark mode".
 *
 * A focus ring is not elevation. AntD draws it with `box-shadow`, so blanket-killing
 * shadows would remove the only focus affordance its controls have. Exported so the test
 * can allow exactly this string and nothing else shaped like it.
 */
export const focusRingShadow = (tokens: SemanticTokens): string => `0 0 0 2px ${tokens.ringSoft}`;

/**
 * NO ELEVATION SHADOWS IN DARK MODE.
 *
 * AntD derives THIRTEEN shadow tokens in `theme/util/alias.js`; only three of them
 * (`boxShadow`, `boxShadowSecondary`, `boxShadowTertiary`) are on the exported
 * `AliasToken` type, which is why the assertion below is required rather than lazy. All
 * thirteen ARE honoured: that file's final statement is
 * `Object.assign({ ...aliasToken }, overrideTokens)`, and `overrideTokens` is the merged
 * ConfigProvider token. Verified against antd 5.29.3.
 */
const FLAT_SHADOWS = {
  boxShadow: 'none',
  boxShadowSecondary: 'none',
  boxShadowTertiary: 'none',
  boxShadowCard: 'none',
  boxShadowDrawerLeft: 'none',
  boxShadowDrawerRight: 'none',
  boxShadowDrawerUp: 'none',
  boxShadowDrawerDown: 'none',
  boxShadowPopoverArrow: 'none',
  boxShadowTabsOverflowLeft: 'none',
  boxShadowTabsOverflowRight: 'none',
  boxShadowTabsOverflowTop: 'none',
  boxShadowTabsOverflowBottom: 'none',
} as Partial<AliasToken>;

/**
 * ⚠️ [C1h §2] THE PALETTE IS A REQUIRED PARAMETER, NEVER A DEFAULTED ONE.
 *
 * [P5c.0] settled this shape for the theme generators and the argument transfers exactly: a
 * default would put the STANDARD palette in the omitted position of a function whose caller
 * may mean either. `antdThemeFor(mode)` with a forgotten argument would compile, build, and
 * ship AntD's controls in the monochrome palette under a colourful stylesheet — a screen half
 * in one design and half in the other, which no unit test in this repository could see,
 * because jsdom resolves no cascade and AntD's values would still all be tokens.
 *
 * ⚠️ AND THE PER-COMPONENT `PRIMARY_FILL` SPREADING BELOW IS WHAT MAKES THE HUE REACH A
 * BUTTON AT ALL — see the seed-token note further down. A colourful `primary` set only at the
 * top level is silently re-derived by AntD's own algorithm and the button renders the wrong
 * indigo, which is exactly the class of failure that note already records.
 */
export const antdThemeFor = (mode: ThemeMode, palette: UiPaletteName): ThemeConfig => {
  const t = uiPalettes[palette][mode];
  const isDark = mode === 'dark';
  const focusRing = focusRingShadow(t);

  /**
   * The primary fill, forced past the seed-derivation described above.
   *
   * Applied per component, because a component token is merged on top of the derived
   * alias rather than being deleted as a seed. Every component that paints a primary
   * SURFACE needs it; the ones that only paint a primary BORDER already take it through
   * their own `activeBorderColor` component token.
   */
  const PRIMARY_FILL = {
    colorPrimary: t.primary,
    colorPrimaryHover: t.primaryHover,
    colorPrimaryActive: t.primaryActive,
    colorPrimaryBorder: t.primary,
    colorPrimaryBorderHover: t.primaryHover,
  } as const;

  return {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    cssVar: true,
    hashed: false,
    token: {
      // ── Algorithm seeds. MUST be opaque — @ant-design/colors parses hex/rgb only,
      //    and `darkAlgorithm` calls `.setA()` on colorTextBase, so an alpha compounds.
      colorPrimary: t.primary,
      colorLink: t.link,
      colorSuccess: t.success,
      colorWarning: t.warning,
      colorError: t.destructive,
      colorInfo: t.info,
      colorBgBase: t.background,
      colorTextBase: t.textBase,

      /*
       * ⚠️ THESE ARE SEEDS, AND ANTD RE-DERIVES THE ALIAS OF THE SAME NAME.
       *
       * `theme/util/alias.js` does `Object.keys(seedToken).forEach(k => delete
       * overrideTokens[k])` — so every SEED name (colorPrimary, colorBgBase, borderRadius,
       * controlHeight, fontSize, …) is used to GENERATE the palette and then the generated
       * value wins for the alias. Measured, not assumed: a `colorPrimary` of #e5e5e5
       * rendered a primary button at #c6c6c6, because `generateColorPalettes` ran a
       * near-white through @ant-design/colors' dark ramp.
       *
       * The fix is per-component overrides, which are merged AFTER and are not stripped.
       * See `PRIMARY_FILL` below. This is exactly the class of defect a browser pass
       * exists to catch: every unit test here passed, and the button was the wrong colour.
       */
      colorPrimaryHover: t.primaryHover,
      colorPrimaryActive: t.primaryActive,
      colorPrimaryBorder: t.borderStrong,
      colorPrimaryBorderHover: t.primary,
      colorPrimaryBg: t.active,
      colorPrimaryBgHover: t.active,
      colorPrimaryText: t.foreground,
      colorPrimaryTextHover: t.foreground,
      colorPrimaryTextActive: t.foreground,
      colorLinkHover: t.link,
      colorLinkActive: t.link,

      // ── Surfaces. Elevation is lightness, so each step is a real, opaque value.
      colorBgLayout: t.background,
      colorBgContainer: t.card,
      colorBgElevated: t.surfaceRaised,
      colorBgSpotlight: t.surfaceRaised,
      colorBgMask: t.overlay,
      colorBgContainerDisabled: t.hover,
      colorBgTextHover: t.hover,
      colorBgTextActive: t.active,

      // ── Borders. AntD's own split, honoured: `colorBorder` bounds CONTROLS,
      //    `colorBorderSecondary` and `colorSplit` draw DIVIDERS. Hairlines, both.
      colorBorder: t.borderStrong,
      colorBorderSecondary: t.borderSubtle,
      colorSplit: t.borderSubtle,

      // ── The full text ramp, every step set rather than derived.
      colorText: t.foreground,
      colorTextSecondary: t.mutedForeground,
      colorTextTertiary: t.textTertiary,
      colorTextQuaternary: t.textTertiary,
      colorTextHeading: t.foreground,
      colorTextLabel: t.mutedForeground,
      colorTextDescription: t.mutedForeground,
      colorTextPlaceholder: t.textTertiary,
      colorTextDisabled: t.textTertiary,
      colorTextLightSolid: t.primaryForeground,
      colorIcon: t.textTertiary,
      colorIconHover: t.foreground,

      // ── The neutral wash ramp: everything AntD tints with, in ONE place.
      //    `colorFillAlter` is where the default grey Table header band comes from.
      colorFill: t.active,
      colorFillSecondary: t.active,
      colorFillTertiary: t.hover,
      colorFillQuaternary: t.hover,
      colorFillAlter: t.hover,
      colorFillContent: t.hover,
      colorFillContentHover: t.active,
      controlItemBgHover: t.hover,
      controlItemBgActive: t.active,
      controlItemBgActiveHover: t.active,
      controlOutline: t.ringSoft,
      controlTmpOutline: t.hover,

      // ── Shape. One anchor in `themeShape`; see tokens.ts for the scale's reading.
      borderRadius: themeShape.radiusPx,
      borderRadiusXS: themeShape.radiusXsPx,
      borderRadiusSM: themeShape.radiusControlPx,
      borderRadiusLG: themeShape.radiusLargePx,
      // Set explicitly: from controlHeight 38 AntD would derive 28.5 and 47.5, and a
      // half-pixel control height is how a filter row ends up visibly misaligned.
      controlHeight: themeShape.controlHeightPx,
      controlHeightXS: 22,
      controlHeightSM: 30,
      controlHeightLG: 44,
      lineWidth: 1,
      lineType: 'solid',
      lineWidthFocus: 2,
      wireframe: false,

      // ── Type.
      fontFamily: themeShape.fontFamily,
      fontFamilyCode: themeShape.fontFamilyMono,
      fontSize: themeShape.fontSizePx,

      ...(isDark ? FLAT_SHADOWS : {}),
    },
    components: {
      /**
       * TABLE — the component that most gives AntD away.
       *
       * The default grey header band is `colorFillAlter`; the sorted column's tint is
       * `bodySortBg`. AntD ships no nth-child zebra, so "no zebra striping" in practice
       * means killing these tinted backgrounds and letting hairline row separators carry
       * the structure instead.
       */
      Table: {
        /*
         * ⚠️ [P4-UX] THE SCROLL EDGE. THE HINT ALREADY EXISTED AND WAS INVISIBLE.
         *
         * rc-table sets `ant-table-ping-right` on mount whenever the body overflows, and
         * AntD draws that edge as `inset -10px 0 8px -8px ${colorSplit}`. Inside Table's
         * styles `colorSplit` feeds exactly two things — that shadow and the sticky
         * scrollbar track — and both are SCROLL AFFORDANCES rather than dividers. At
         * `borderSubtle` (7% white) the shadow is not perceptible on a near-black canvas,
         * which is precisely what the QA report meant by "no scrollbar and no hint": the
         * table WAS telling you, in a colour nobody can see. Row separators are unaffected;
         * they come from `borderColor` below.
         */
        colorSplit: t.borderStrong,
        headerBg: 'transparent',
        headerColor: t.mutedForeground,
        headerSplitColor: 'transparent',
        headerBorderRadius: 0,
        headerSortActiveBg: 'transparent',
        headerSortHoverBg: t.hover,
        fixedHeaderSortActiveBg: 'transparent',
        bodySortBg: 'transparent',
        rowHoverBg: t.hover,
        rowSelectedBg: t.active,
        rowSelectedHoverBg: t.active,
        rowExpandedBg: 'transparent',
        footerBg: 'transparent',
        footerColor: t.mutedForeground,
        borderColor: t.borderSubtle,
        headerFilterHoverBg: t.hover,
        filterDropdownBg: t.surfaceRaised,
        filterDropdownMenuBg: t.surfaceRaised,
        expandIconBg: 'transparent',
        stickyScrollBarBg: t.borderStrong,
        stickyScrollBarBorderRadius: themeShape.radiusXsPx,
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
        cellPaddingBlockMD: 10,
        cellPaddingInlineMD: 14,
        cellPaddingBlockSM: 8,
        cellPaddingInlineSM: 10,
        cellFontSize: themeShape.fontSizePx,
        cellFontSizeMD: themeShape.fontSizePx,
        cellFontSizeSM: 13,
        selectionColumnWidth: 44,
      },

      /**
       * BUTTON — `defaultBg` is transparent, not a surface.
       *
       * AntD REPLACES the background on hover rather than layering, so a wash over an
       * opaque `defaultBg` composites over the CARD and comes out darker instead of
       * lighter. Transparent + a wash lightens correctly in both modes, and a transparent
       * secondary button is the near-black idiom anyway.
       */
      Button: {
        ...PRIMARY_FILL,
        defaultBg: 'transparent',
        defaultColor: t.foreground,
        defaultBorderColor: t.borderStrong,
        defaultHoverBg: t.hover,
        defaultHoverColor: t.foreground,
        defaultHoverBorderColor: t.borderStrong,
        defaultActiveBg: t.active,
        defaultActiveColor: t.foreground,
        defaultActiveBorderColor: t.borderStrong,
        borderColorDisabled: t.borderSubtle,
        ghostBg: 'transparent',
        defaultGhostColor: t.foreground,
        defaultGhostBorderColor: t.borderStrong,
        primaryColor: t.primaryForeground,
        solidTextColor: t.primaryForeground,
        textTextColor: t.foreground,
        textTextHoverColor: t.foreground,
        textTextActiveColor: t.foreground,
        textHoverBg: t.hover,
        linkHoverBg: 'transparent',
        defaultShadow: 'none',
        primaryShadow: 'none',
        dangerShadow: 'none',
        fontWeight: 500,
        contentFontSize: themeShape.fontSizePx,
        paddingInline: 14,
        paddingInlineSM: 10,
        paddingInlineLG: 18,
        onlyIconSize: 16,
      },

      Input: {
        activeBg: t.card,
        hoverBg: t.card,
        addonBg: t.hover,
        hoverBorderColor: t.borderStrong,
        activeBorderColor: t.primary,
        activeShadow: focusRing,
        errorActiveShadow: 'none',
        warningActiveShadow: 'none',
        paddingBlock: 8,
        paddingInline: 12,
        paddingBlockSM: 4,
        paddingInlineSM: 8,
        paddingBlockLG: 10,
        paddingInlineLG: 14,
        inputFontSize: themeShape.fontSizePx,
      },

      Select: {
        selectorBg: t.card,
        clearBg: t.card,
        hoverBorderColor: t.borderStrong,
        activeBorderColor: t.primary,
        activeOutlineColor: t.ringSoft,
        optionSelectedBg: t.active,
        optionSelectedColor: t.foreground,
        optionSelectedFontWeight: 500,
        optionActiveBg: t.hover,
        optionPadding: '6px 12px',
        optionHeight: 32,
        optionFontSize: themeShape.fontSizePx,
      },

      // Inherits Input's shared control token bucket but does NOT share its values —
      // they are separate buckets, so the field styling is repeated here on purpose.
      DatePicker: {
        activeBg: t.card,
        hoverBg: t.card,
        hoverBorderColor: t.borderStrong,
        activeBorderColor: t.primary,
        activeShadow: focusRing,
        errorActiveShadow: 'none',
        warningActiveShadow: 'none',
        paddingBlock: 8,
        paddingInline: 12,
        inputFontSize: themeShape.fontSizePx,
        cellHoverBg: t.hover,
        cellActiveWithRangeBg: t.active,
        cellHoverWithRangeBg: t.active,
        cellBgDisabled: 'transparent',
        cellRangeBorderColor: t.primary,
        cellHeight: 28,
        cellWidth: 36,
      },

      /**
       * MENU — the `dark*` keys matter even though the whole app has a dark mode.
       *
       * AntD's `Menu theme="dark"` defaults to its own #001529 navy family, which would
       * be a rogue colour in a near-black shell the moment anyone writes `theme="dark"`.
       * Pre-empted rather than discovered.
       */
      Menu: {
        itemBg: 'transparent',
        subMenuItemBg: 'transparent',
        popupBg: t.surfaceRaised,
        itemColor: t.mutedForeground,
        itemHoverColor: t.foreground,
        itemHoverBg: t.hover,
        itemSelectedColor: t.foreground,
        itemSelectedBg: t.active,
        itemActiveBg: t.active,
        itemDisabledColor: t.textTertiary,
        subMenuItemSelectedColor: t.foreground,
        groupTitleColor: t.textTertiary,
        horizontalItemHoverBg: t.hover,
        horizontalItemSelectedBg: t.active,
        horizontalItemSelectedColor: t.foreground,
        horizontalItemBorderRadius: themeShape.radiusControlPx,
        itemBorderRadius: themeShape.radiusControlPx,
        subMenuItemBorderRadius: themeShape.radiusControlPx,
        itemHeight: 34,
        itemMarginInline: 4,
        itemMarginBlock: 2,
        itemPaddingInline: 10,
        activeBarBorderWidth: 0,
        activeBarHeight: 2,
        iconSize: 16,
        collapsedIconSize: 16,
        dangerItemColor: t.destructive,
        darkItemBg: 'transparent',
        darkSubMenuItemBg: 'transparent',
        darkPopupBg: t.surfaceRaised,
        darkItemColor: t.mutedForeground,
        darkItemHoverColor: t.foreground,
        darkItemHoverBg: t.hover,
        darkItemSelectedColor: t.foreground,
        darkItemSelectedBg: t.active,
        darkGroupTitleColor: t.textTertiary,
        darkItemDisabledColor: t.textTertiary,
      },

      Modal: {
        contentBg: t.surfaceRaised,
        headerBg: t.surfaceRaised,
        footerBg: 'transparent',
        titleColor: t.foreground,
        titleFontSize: 16,
        titleLineHeight: 1.5,
      },

      Drawer: {
        footerPaddingBlock: 12,
        footerPaddingInline: 20,
      },

      /**
       * TABS — `inkBarColor` is the foreground, NOT the primary.
       *
       * The design language reserves the strongest value for primary buttons and links;
       * a selected-tab underline is a state, not a call to action. One line to change if
       * that reading is ever revisited.
       */
      Tabs: {
        itemColor: t.mutedForeground,
        itemHoverColor: t.foreground,
        itemSelectedColor: t.foreground,
        itemActiveColor: t.foreground,
        inkBarColor: t.foreground,
        cardBg: t.card,
        cardHeight: 36,
        cardGutter: 4,
        cardPadding: '8px 14px',
        titleFontSize: themeShape.fontSizePx,
        horizontalItemPadding: '10px 0',
        horizontalItemGutter: 20,
        horizontalMargin: '0 0 16px 0',
      },

      Tag: {
        defaultBg: t.hover,
        defaultColor: t.mutedForeground,
        borderRadiusSM: themeShape.radiusXsPx,
      },

      Segmented: {
        trackBg: t.hover,
        trackPadding: 2,
        itemColor: t.mutedForeground,
        itemHoverColor: t.foreground,
        itemHoverBg: 'transparent',
        itemActiveBg: t.active,
        itemSelectedBg: t.surfaceRaised,
        itemSelectedColor: t.foreground,
      },

      Layout: {
        headerBg: t.card,
        bodyBg: t.background,
        siderBg: t.card,
        footerBg: t.background,
        lightSiderBg: t.card,
        headerHeight: 56,
        headerPadding: '0 16px',
        headerColor: t.foreground,
        triggerBg: t.surfaceRaised,
        triggerColor: t.mutedForeground,
      },

      // ── Not in the brief's list, but rendered on every screen this app has. Left to
      //    inherit, they keep AntD's default surfaces and paddings.
      Card: {
        headerBg: 'transparent',
        headerFontSize: 15,
        bodyPadding: 20,
        headerPadding: 20,
        extraColor: t.mutedForeground,
        actionsBg: 'transparent',
      },

      Form: {
        labelColor: t.mutedForeground,
        labelRequiredMarkColor: t.destructive,
        labelFontSize: 13,
        itemMarginBottom: 20,
        verticalLabelPadding: '0 0 6px',
      },

      Tree: {
        nodeHoverBg: t.hover,
        nodeSelectedBg: t.active,
        directoryNodeSelectedBg: t.active,
        directoryNodeSelectedColor: t.foreground,
        titleHeight: 28,
      },

      Pagination: {
        ...PRIMARY_FILL,
        itemBg: 'transparent',
        itemActiveBg: t.active,
        itemActiveColorDisabled: t.textTertiary,
        itemLinkBg: 'transparent',
        itemInputBg: t.card,
        colorPrimary: t.foreground,
        colorPrimaryHover: t.foreground,
      },

      Alert: {
        withDescriptionPadding: '16px 20px',
        defaultPadding: '10px 16px',
      },

      Tooltip: {
        colorBgSpotlight: t.surfaceRaised,
        colorTextLightSolid: t.foreground,
      },

      Dropdown: {
        paddingBlock: 6,
      },

      Empty: {
        colorTextDescription: t.mutedForeground,
      },

      Spin: {
        colorPrimary: t.foreground,
      },

      // Every remaining surface that paints the primary fill. Grouped rather than spread
      // through the list above so the seed-derivation trap has one obvious antidote.
      Checkbox: { ...PRIMARY_FILL },
      Radio: { ...PRIMARY_FILL },
      Switch: { ...PRIMARY_FILL },
      Slider: { ...PRIMARY_FILL },
      Progress: { ...PRIMARY_FILL },
      Steps: { ...PRIMARY_FILL },
    },
  };
};
